import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type FilterOperatorObject,
} from '@/components/DataTable';
import {
  PROJECT_FILTER_OPTION_FIELDS,
  type ProjectDataTableItem,
} from '@kirin/api-client/contracts/projects';
import {
  ProjectNameCell,
  RepoUrlCell,
  UsersCountCell,
  AiInstructionsCell,
  HooksCell,
} from './CellRenderers';
import { PROJECTS_FILTER_URL_KEYS } from './constants';
import {
  fetchProjectFilterOptions,
  getProjectFilterOptionsQueryKey,
} from './api';

const FILTER_OPTIONS_PAGE_SIZE = 10;

// String columns (name, repo URL) behave like the Extensions "name" filter:
// pick from a dynamic dropdown of existing values (INCLUDES / EXCLUDES) or type
// a contains match. The backend maps all of these to ILIKE / IN.
const StringOperators: FilterOperatorObject[] = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
];

/**
 * Column definitions for the Projects DataTable (KIR-3356).
 *
 * Name + Repo URL are filterable via a server-backed dynamic options dropdown
 * (distinct values) — the Extensions-name filter pattern. Other columns are
 * sort-only. Global search also covers name + repo URL.
 */
export function createProjectColumns(
  organizationId: string,
  onSelectProject?: (project: ProjectDataTableItem) => void
): ColumnConfig<ProjectDataTableItem>[] {
  return [
    {
      id: 'name',
      header: 'Project name',
      accessor: 'name',
      width: 240,
      renderCell: ({ row }) => (
        <ProjectNameCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'name',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: { urlKey: PROJECTS_FILTER_URL_KEYS.name },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getProjectFilterOptionsQueryKey(
            organizationId,
            PROJECT_FILTER_OPTION_FIELDS.NAME,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchProjectFilterOptions(
            organizationId,
            PROJECT_FILTER_OPTION_FIELDS.NAME,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: { mode: 'offset', limit: FILTER_OPTIONS_PAGE_SIZE },
      },
    },
    {
      id: 'repoUrl',
      header: 'Repo URL',
      accessor: 'repoOriginUrl',
      width: 280,
      renderCell: ({ row }) => <RepoUrlCell row={row} />,
      sortable: true,
      sortField: 'repoUrl',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: { urlKey: PROJECTS_FILTER_URL_KEYS.repoUrl },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getProjectFilterOptionsQueryKey(
            organizationId,
            PROJECT_FILTER_OPTION_FIELDS.REPO_URL,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchProjectFilterOptions(
            organizationId,
            PROJECT_FILTER_OPTION_FIELDS.REPO_URL,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: { mode: 'offset', limit: FILTER_OPTIONS_PAGE_SIZE },
      },
    },
    {
      id: 'users',
      header: 'Users',
      accessor: 'userCount',
      width: 100,
      renderCell: ({ row }) => (
        <UsersCountCell row={row} onSelectProject={onSelectProject} />
      ),
      sortable: true,
      sortField: 'users',
    },
    {
      id: 'aiInstructions',
      header: 'AI Instructions',
      accessor: 'aiInstructionsCount',
      width: 180,
      renderCell: ({ row }) => (
        <AiInstructionsCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'aiInstructions',
    },
    {
      id: 'hooks',
      header: 'Hooks',
      accessor: 'hooksCount',
      width: 120,
      renderCell: ({ row }) => (
        <HooksCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'hooks',
    },
  ];
}
